import {
  Component,
  inject,
  OnInit,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { ManufacturerService } from './manufacturers.service';
import { Subject, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  catchError,
  tap,
} from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'manufacturers',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './manufacturers-component.html',
  styleUrls: ['./manufacturers-component.scss'],
})
export class ManufacturersComponent implements OnInit {
  manufacturers: any[] = [];
  manufacturersLoading = false;
  searchTerm = '';
  pageSize = 10;
  currentPage = 1;
  pageCount = 1;
  manufacturerToDelete: any = null;
  uploadUrl = `${environment.apiUrl}/manufacturers/bulk-upload`;

  // Dependency Injection
  private manufacturerService = inject(ManufacturerService);
  private router = inject(Router);
  private modalService = inject(NgbModal);
  private toastr = inject(ToastrService);
  private searchSubject = new Subject<string>();

  ngOnInit(): void {
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => {
          this.currentPage = 1;
          this.manufacturersLoading = true;
        }),
        switchMap((term) =>
          this.manufacturerService.searchManufacturersByName(term).pipe(
            catchError(() => of([])),
            tap(() => (this.manufacturersLoading = false))
          )
        )
      )
      .subscribe((response: any) => {
        // ✅ Handle flat array response from search API
        this.manufacturers = (response || [])
          .map((m: any) => ({ ...m, isContactsExpanded: false }))
          .filter((m: any) => m.isActive);
        this.pageCount = 1; // reset pagination for search
      });

    this.loadManufacturers(); // initial load without search
  }

  loadManufacturers(): void {
    this.manufacturersLoading = true;
    const params = {
      page: this.currentPage,
      limit: this.pageSize,
      search: this.searchTerm || undefined,
    };
    this.manufacturerService.getManufacturers(params).subscribe({
      next: (response: any) => {
        this.manufacturers = (response.data || [])
          .map((m: any) => ({ ...m, isContactsExpanded: false }))
          .filter((m: any) => m.isActive);

        this.pageCount = response.pageCount || 1;
        this.manufacturersLoading = false;
      },
      error: (err) => {
        console.error('Error loading manufacturers:', err);
        this.toastr.error('Failed to load manufacturers');
        this.manufacturersLoading = false;
      },
    });
  }

  onSearch(): void {
    const trimmed = this.searchTerm.trim();
    if (trimmed) {
      this.searchSubject.next(trimmed);
    } else {
      this.loadManufacturers();
    }
  }

  pageSizeChange(): void {
    this.currentPage = 1;
    this.loadManufacturers();
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.pageCount) return;
    this.currentPage = page;
    this.loadManufacturers();
  }

  getVisiblePages(): number[] {
    const pages: number[] = [];
    const start = Math.max(2, this.currentPage - 2);
    const end = Math.min(this.pageCount - 1, this.currentPage + 2);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  openDeleteConfirmationModal(content: any, manufacturer: any): void {
    this.manufacturerToDelete = manufacturer;
    this.modalService.open(content, { centered: true });
  }

  closeModal(): void {
    this.modalService.dismissAll();
  }

  confirmDelete(): void {
    const id = this.manufacturerToDelete?.id;
    if (!id) return;

    this.manufacturerService.deleteManufacturer(id).subscribe({
      next: () => {
        this.toastr.success('Manufacturer deleted successfully');
        this.closeModal();
        this.loadManufacturers();
      },
      error: (err) => {
        console.error('Delete error:', err);
        this.toastr.error('Failed to delete manufacturer');
      },
    });
  }

  @ViewChild('fileInput') fileInput!: ElementRef;
  uploadType: 'CREATE' | 'BRANDS_CONTACTS' = 'CREATE';

  trackById(index: number, item: any): string {
    return item.id;
  }

  // Download manufacturer creation template
  downloadCreationTemplate(): void {
    this.manufacturerService.downloadTemplate().subscribe({
      next: (blob: any) => {
        this.triggerDownload(
          blob,
          'manufacturer-creation-template.xlsx',
          'Template downloaded successfully!'
        );
      },
      error: (error: any) => {
        console.error('Error downloading creation template:', error);
        this.toastr.error('Failed to download creation template.');
      },
    });
  }

  // Download Brands & Contacts template
  downloadBrandsContactsTemplate(): void {
    this.manufacturerService.downloadBrandsContactsTemplate().subscribe({
      next: (blob: any) => {
        this.triggerDownload(
          blob,
          'brands-contacts-template.xlsx',
          'Template downloaded successfully!'
        );
      },
      error: (error: any) => {
        console.error('Error downloading brands & contacts template:', error);
        this.toastr.error('Failed to download brands & contacts template.');
      },
    });
  }

  // Open export confirmation modal
  openExportModal(content: any) {
    this.modalService.open(content, { centered: true });
  }

  // Export manufacturers to Excel
  exportToExcel(): void {
    this.manufacturerService.exportManufacturers().subscribe({
      next: (blob: any) => {
        this.triggerDownload(
          blob,
          'manufacturers_export.xlsx',
          'Manufacturers exported successfully!'
        );
        this.closeModal();
      },
      error: (error: any) => {
        console.error('Error exporting manufacturers:', error);
        this.toastr.error('Failed to export manufacturers.');
        this.closeModal();
      },
    });
  }

  // Helper to trigger file download
  private triggerDownload(
    blob: Blob,
    fileName: string,
    successMessage: string
  ) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    window.URL.revokeObjectURL(url);
    this.toastr.success(successMessage);
  }

  // Trigger file input for upload
  triggerUpload(type: 'CREATE' | 'BRANDS_CONTACTS'): void {
    this.uploadType = type;
    this.fileInput.nativeElement.value = ''; // Reset input
    this.fileInput.nativeElement.click();
  }

  // Handle file selection
  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      this.toastr.error('Invalid file format. Please upload an Excel file.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    if (this.uploadType === 'CREATE') {
      this.manufacturerService.bulkUpload(formData).subscribe({
        next: (res) => this.onUploadSuccess('Bulk creation successful!'),
        error: (err) => this.onUploadError(err),
      });
    } else {
      this.manufacturerService.bulkBrandsContactsUpload(formData).subscribe({
        next: (res) =>
          this.onUploadSuccess('Bulk brands & contacts upload successful!'),
        error: (err) => this.onUploadError(err),
      });
    }
  }

  // Handle successful bulk upload
  onUploadSuccess(message: string): void {
    this.toastr.success(message);
    this.loadManufacturers(); // Reload manufacturers list
  }

  // Handle bulk upload error
  onUploadError(event: any): void {
    console.error('Bulk upload error:', event);
    this.toastr.error('Failed to upload manufacturers.');
  }

  toggleBrands(manufacturer: any): void {
    manufacturer.isBrandsExpanded = !manufacturer.isBrandsExpanded;
  }

  toggleContacts(manufacturer: any): void {
    manufacturer.isContactsExpanded = !manufacturer.isContactsExpanded;
  }
}
